import { useEffect, useRef, useState } from "react";
import api, { fmt, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ScanText, Upload, FileText, Trash2, AlertTriangle, PackagePlus, Loader2, FolderTree, Sparkles } from "lucide-react";

const SEV = {
  alta: "bg-destructive/15 text-destructive border-destructive/40",
  media: "bg-amber-500/15 text-amber-500 border-amber-500/40",
  baja: "bg-muted text-muted-foreground border-border",
};

export default function LicitacionesView() {
  const { user, access } = useAuth();
  const canWrite = user?.role !== "consulta" && (access?.active ?? true);
  const [list, setList] = useState([]);
  const [current, setCurrent] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [keySet, setKeySet] = useState(true);
  const fileRef = useRef();

  const loadList = async () => { const { data } = await api.get("/licitaciones"); setList(data); };
  const loadSettings = async () => { const { data } = await api.get("/settings"); setKeySet(data.gemini_key_set); };
  useEffect(() => { loadList(); loadSettings(); }, []);

  const analyze = async (file) => {
    if (!file) return;
    setAnalyzing(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/licitaciones/analizar", form, { headers: { "Content-Type": "multipart/form-data" } });
      setCurrent(data);
      toast.success(`Analizado: ${data.conceptos.length} conceptos detectados`);
      loadList();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAnalyzing(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const openOne = async (id) => { const { data } = await api.get(`/licitaciones/${id}`); setCurrent(data); };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este análisis?")) return;
    await api.delete(`/licitaciones/${id}`);
    if (current?.id === id) setCurrent(null);
    loadList();
  };
  const importar = async () => {
    try {
      const { data } = await api.post(`/licitaciones/${current.id}/importar`);
      toast.success(`Importados ${data.creados} conceptos al catálogo (${data.omitidos} ya existían)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const importarPresupuesto = async () => {
    try {
      const { data } = await api.post(`/licitaciones/${current.id}/importar-presupuesto`);
      toast.success(`Presupuesto creado: "${data.nombre}" con ${data.conceptos} conceptos. Ábrelo en la sección Presupuesto.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const estimarPrecios = async () => {
    setEstimating(true);
    try {
      const { data } = await api.post(`/licitaciones/${current.id}/estimar-precios`);
      setCurrent((c) => ({ ...c, conceptos: data.conceptos }));
      toast.success(`Precios estimados con IA para ${data.estimados} conceptos`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setEstimating(false); }
  };

  return (
    <div className="flex flex-1 min-h-0" data-testid="licitaciones-view">
      <div className="w-72 shrink-0 border-r border-border flex flex-col">
        <div className="h-12 border-b border-border flex items-center px-3 gap-2">
          <ScanText className="h-4 w-4 text-primary" />
          <span className="font-head font-bold text-sm">Licitaciones IA</span>
        </div>
        <div className="p-3 border-b border-border">
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden" data-testid="licitacion-file-input"
            onChange={(e) => e.target.files[0] && analyze(e.target.files[0])} />
          <Button className="w-full rounded-sm gap-2" disabled={!canWrite || analyzing} onClick={() => fileRef.current?.click()} data-testid="upload-licitacion-btn">
            {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {analyzing ? "Analizando…" : "Subir PDF de licitación"}
          </Button>
          {!keySet && <p className="text-[11px] text-amber-500 mt-2">Configura tu API key de Gemini en Administración → IA.</p>}
          {!canWrite && <p className="text-[11px] text-muted-foreground mt-2">Requiere suscripción activa.</p>}
        </div>
        <div className="overflow-y-auto flex-1">
          {list.map((l) => (
            <button key={l.id} onClick={() => openOne(l.id)} data-testid={`licitacion-item-${l.id}`}
              className={`w-full text-left px-3 py-2 border-b border-border/60 transition-colors ${current?.id === l.id ? "bg-accent/60 border-l-2 border-l-primary" : "hover:bg-accent/40 border-l-2 border-l-transparent"}`}>
              <div className="text-sm font-medium truncate flex items-center gap-1"><FileText className="h-3.5 w-3.5 shrink-0" /> {l.titulo || l.filename}</div>
              <div className="text-xs text-muted-foreground truncate">{l.conceptos?.length || 0} conceptos · {new Date(l.created_at).toLocaleDateString("es-MX")}</div>
            </button>
          ))}
          {list.length === 0 && <div className="p-4 text-xs text-muted-foreground">Aún no hay análisis. Sube un PDF de Compranet.</div>}
        </div>
      </div>

      <div className="flex-1 min-w-0 overflow-auto">
        {!current ? (
          <div className="p-10 text-muted-foreground text-sm">Sube un PDF de licitación o selecciona un análisis previo.</div>
        ) : (
          <div className="p-5 space-y-5" data-testid="licitacion-result">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h1 className="font-head font-black text-lg tracking-tight">{current.titulo || current.filename}</h1>
                <p className="text-sm text-muted-foreground max-w-2xl mt-1">{current.resumen}</p>
              </div>
              <div className="flex gap-1">
                {canWrite && <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={estimarPrecios} disabled={estimating} data-testid="estimar-precios-btn">{estimating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Estimar precios (IA)</Button>}
                {canWrite && <Button size="sm" className="rounded-sm gap-1" onClick={importarPresupuesto} data-testid="crear-presupuesto-btn"><FolderTree className="h-4 w-4" /> Crear presupuesto</Button>}
                {canWrite && <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={importar} data-testid="importar-conceptos-btn"><PackagePlus className="h-4 w-4" /> Importar al catálogo</Button>}
                {canWrite && <Button size="sm" variant="outline" className="rounded-sm text-destructive" onClick={() => remove(current.id)} data-testid="delete-licitacion-btn"><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>

            {current.clausulas_riesgo?.length > 0 && (
              <div className="border border-border rounded-sm">
                <div className="px-3 h-9 flex items-center gap-2 border-b border-border bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> Cláusulas de riesgo detectadas
                </div>
                <div className="p-3 space-y-2">
                  {current.clausulas_riesgo.map((c, i) => (
                    <div key={i} className="text-sm border border-border/60 rounded-sm p-2" data-testid={`clausula-${i}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">{c.clausula}</span>
                        <span className={`text-[10px] uppercase px-2 py-0.5 rounded-sm border ${SEV[(c.severidad || "baja").toLowerCase()] || SEV.baja}`}>{c.severidad}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{c.riesgo}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="border border-border rounded-sm overflow-hidden">
              <div className="px-3 h-9 flex items-center border-b border-border bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                Catálogo de conceptos ({current.conceptos?.length || 0})
              </div>
              <table className="qc-table">
                <thead>
                  <tr>
                    <th style={{ width: 100 }}>Clave</th>
                    <th>Descripción</th>
                    <th style={{ width: 60 }}>Unidad</th>
                    <th style={{ width: 110 }} className="text-right">Cantidad</th>
                    <th style={{ width: 120 }} className="text-right">P.U. est.</th>
                  </tr>
                </thead>
                <tbody>
                  {current.conceptos?.map((c, i) => (
                    <tr key={i} data-testid={`lic-concepto-${i}`}>
                      <td className="font-mono-num text-primary text-xs">{c.codigo}</td>
                      <td className="text-xs">{c.descripcion}</td>
                      <td className="font-mono-num text-xs text-muted-foreground">{c.unidad}</td>
                      <td className="text-right font-mono-num">{fmt(c.cantidad, 2)}</td>
                      <td className="text-right font-mono-num text-xs">{c.pu_estimado != null ? `$ ${fmt(c.pu_estimado, 2)}` : "—"}</td>
                    </tr>
                  ))}
                  {(!current.conceptos || current.conceptos.length === 0) && <tr><td colSpan={5} className="text-center text-muted-foreground py-6 text-xs">No se detectaron conceptos.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
